/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { css, jsx } from '@emotion/react'
import { Component, Fragment } from 'react'
import { FormattedMessage, injectIntl } from 'react-intl'
import Sticky from 'react-sticky-el'
// eslint-disable-next-line no-restricted-imports
import { withLDConsumer } from 'launchdarkly-react-client-sdk'

import type { WithEuiThemeProps } from '@elastic/eui'
import {
  EuiSplitPanel,
  EuiFlexGroup,
  EuiFlexItem,
  EuiSpacer,
  EuiHorizontalRule,
  EuiSkeletonText,
  EuiButtonEmpty,
  EuiAccordion,
  EuiPanel,
  EuiIconTip,
  EuiText,
  withEuiTheme,
} from '@elastic/eui'

import type {
  StackVersionConfig,
  DeploymentSearchResponse,
  DeploymentClaimRequest,
  DeploymentClaimResponse,
} from '@modules/cloud-api/v1/types'
import { AjaxRequestError } from '@modules/ui-types'
import type {
  ClusterSnapshot,
  AsyncRequestState,
  ProfileState,
  RegionId,
  AnyTopologyElement,
  AsyncRequestError,
  StackDeployment,
} from '@modules/ui-types'
import { CuiAlert } from '@modules/cui/Alert'
import { getResponseStatus } from '@modules/utils/ajax'

import PriceButton from '@/components/StackDeploymentEditor/CreateStackDeploymentEditor/PriceButton'
import DeploymentArchitecture from '@/components/DeploymentArchitecture/DeploymentArchitecture'
import { getDeploymentNodeConfigurations } from '@/lib/stackDeployments/selectors/topologyElements'
import { getLatestSnapshotSuccess } from '@/lib/stackDeployments/selectors/snapshots'
import { setAutoscalingDisabled } from '@/lib/stackDeployments/autoscaling'
import {
  getRegionId,
  getFirstEsClusterFromGet,
  getRegionIdForCreate,
} from '@/lib/stackDeployments/selectors/fundamentals'
import { getAutoscalingStatus } from '@/lib/stackDeployments/selectors/autoscaling'
import { getUpsertVersion } from '@/lib/stackDeployments/selectors/creates'
import { inTrial, inActiveTrial } from '@/lib/trial'
import {
  setEsSettings,
  getDeploymentVersionSetter,
  getDeploymentNameSetter,
  getCreatePayload,
  changeRestoreFromSnapshot,
} from '@/lib/stackDeployments/crud'

import CreateStackDeploymentTitle from '../CreateStackDeploymentTitle'

import CreateDeploymentButton from './CreateDeploymentButton'
import ConfigureDeploymentSteps from './ConfigureDeploymentSteps'
import SelectTemplate from './SelectTemplate'
import SolutionViewWarningCallout from './SolutionViewWarningCallout'

import type { LDProps } from 'launchdarkly-react-client-sdk/src/withLDConsumer'
import type { CreateEditorComponentConsumerProps as ConsumerProps } from '../types'
import type { WrappedComponentProps } from 'react-intl'

type StateProps = {
  showPrice: boolean
  stackVersions: StackVersionConfig[] | undefined
  createStackDeploymentRequest: AsyncRequestState
  claimInstantStackDeploymentRequest: AsyncRequestState
  trialMaxedOut: boolean
  profile: ProfileState
  isLaunchdarklyEnabled: boolean
  isUserConsole: boolean
}

interface DispatchProps {
  claimInstantStackDeployment: (params: { payload: DeploymentClaimRequest }) => Promise<any>
  fetchDeployment: (stackDeploymentId: string) => Promise<any>
}

export type Props = StateProps & DispatchProps & ConsumerProps

type State = {
  claimedInstantStackDeploymentId: string | null
  restoreSnapshotSource: DeploymentSearchResponse | null
  showArchitectureSummary: boolean
  priceViewSelected: `hourly` | `monthly`
}

class CreateStackDeploymentEditor extends Component<
  Props & WrappedComponentProps & LDProps & WithEuiThemeProps,
  State
> {
  state: State = {
    restoreSnapshotSource: null,
    showArchitectureSummary: false,
    priceViewSelected: `hourly`,
    claimedInstantStackDeploymentId: null,
  }

  render() {
    const {
      profile,
      showTrialExperience,
      initialSpacer = true,
      deploymentTitle,
      theme,
    } = this.props

    /* only show settings and advanced settings link if
     *   a) the user isn't in a trial
     *   b) the user is in a trial but already created at least one deployment
     */
    const showSettings = !inTrial({ profile }) || inActiveTrial({ profile })

    return (
      <div>
        {initialSpacer && <EuiSpacer size='l' />}
        <EuiSplitPanel.Outer
          css={css({
            width: 'auto',
            margin: '0 auto',
            maxWidth: 820,
          })}
        >
          <EuiSplitPanel.Inner
            css={css({
              padding: `${theme.euiTheme.size.xxxxl} ${theme.euiTheme.size.xxxxl} ${theme.euiTheme.size.s}`,
              [`@media screen and (max-width: ${theme.euiTheme.breakpoint.m}px)`]: {
                padding: `${theme.euiTheme.size.xl} ${theme.euiTheme.size.s}`,
              },
            })}
          >
            {deploymentTitle ?? (
              <CreateStackDeploymentTitle showTrialExperience={showTrialExperience} />
            )}
            <EuiHorizontalRule margin='s' />
          </EuiSplitPanel.Inner>
          {this.renderPage(showSettings)}
          {this.renderFooter(showSettings)}
        </EuiSplitPanel.Outer>
      </div>
    )
  }

  renderPage(showSettings: boolean) {
    const {
      availableVersions,
      createStackDeploymentRequest,
      editorState,
      onChange,
      region,
      setDeploymentTemplate,
      setRegion,
      showPrice,
      showRegion,
      trialMaxedOut,
      whitelistedVersions,
      globalDeploymentTemplates,
      setGlobalTemplate,
      deploymentTemplates,
      isUserConsole,
      setSnapshotSettingsDisabled,
      theme,
    } = this.props

    const { restoreSnapshotSource } = this.state

    const { deployment } = editorState
    const isAutoscalingMLEnabled = getAutoscalingStatus({ deployment }) === `ml`
    const showMLAutoscalingBell = isAutoscalingMLEnabled && isUserConsole

    const version = getUpsertVersion(editorState)

    return (
      <EuiSplitPanel.Inner
        css={css({
          padding: `${theme.euiTheme.size.s} ${theme.euiTheme.size.xxxxl}`,
          [`@media screen and (max-width: ${theme.euiTheme.breakpoint.m}px)`]: {
            padding: theme.euiTheme.size.s,
          },
        })}
      >
        <SelectTemplate
          disabled={createStackDeploymentRequest.inProgress}
          editorState={editorState}
          globalDeploymentTemplates={globalDeploymentTemplates}
          setDeploymentName={getDeploymentNameSetter({ onChange })}
          setDeploymentTemplate={setDeploymentTemplate}
          setGlobalTemplate={setGlobalTemplate}
          availableVersions={availableVersions}
          whitelistedVersions={whitelistedVersions}
          setVersion={getDeploymentVersionSetter({ editorState, onChange })}
          setRegion={setRegion}
          showRegion={showRegion!}
          region={region}
          showPrice={showPrice}
          onChange={onChange}
          onChangeSnapshotSource={this.onChangeSnapshotSource}
          onChangeSnapshot={this.onChangeSnapshot}
          setEsSettings={this.updateEsSettings}
          restoreFromSnapshot={restoreSnapshotSource !== null}
          trialMaxedOut={trialMaxedOut}
          deploymentTemplates={deploymentTemplates}
          showSettings={showSettings}
          setSnapshotSettingsDisabled={setSnapshotSettingsDisabled}
        />

        <SolutionViewWarningCallout version={version} />

        <EuiSpacer size='l' />

        {showSettings && (
          <Fragment>
            <EuiAccordion
              id='configure-deployment-steps-accordion'
              data-test-id='configure-deployment-steps-accordion'
              buttonContent={
                <FormattedMessage
                  id='create-deployment-from-template.advanced-settings'
                  defaultMessage='Advanced settings'
                />
              }
              arrowProps={{ css: { color: theme.euiTheme.colors.primary } }}
              buttonProps={{
                css: { color: theme.euiTheme.colors.primary },
                /* @ts-expect-error data-test-id is not declared on the EuiButton */
                'data-test-id': 'configure-deployment-steps',
              }}
              paddingSize='xs'
              extraAction={
                showMLAutoscalingBell && (
                  <EuiFlexGroup gutterSize='xs' alignItems='center'>
                    <EuiText size='s' color='subdued'>
                      <FormattedMessage
                        defaultMessage='ML nodes autoscaling enabled'
                        id='create-deployment-from-template.ml-autoscaling-enabled'
                      />
                    </EuiText>
                    <EuiIconTip
                      type='iInCircle'
                      color='subdued'
                      content={
                        <FormattedMessage
                          id='create-deployment-from-template.ml-autoscaling-enabled.tip'
                          defaultMessage={`This is a default setting. You can change it later in Kibana's advanced settings.`}
                        />
                      }
                    />
                  </EuiFlexGroup>
                )
              }
            >
              <EuiSpacer size='l' />
              <ConfigureDeploymentSteps
                editorState={editorState}
                onChange={onChange}
                firstStepNumber={this.getSelectTemplateStepCount() + 1}
                region={region!}
                snapshotDetails={this.getSnapshotDetails()}
                showPrice={showPrice}
              />
            </EuiAccordion>
            <EuiSpacer size='l' />
          </Fragment>
        )}
      </EuiSplitPanel.Inner>
    )
  }

  renderFooter(showSettings: boolean) {
    const {
      createStackDeploymentRequest,
      claimInstantStackDeploymentRequest,
      editorState,
      region,
      onCreateDeployment,
      theme,
    } = this.props
    const { claimedInstantStackDeploymentId } = this.state

    const disableBottomNav = this.shouldDisableBottomNav()
    const { deploymentTemplate } = editorState
    const isFooterButtonDisabled =
      disableBottomNav ||
      createStackDeploymentRequest.inProgress ||
      claimInstantStackDeploymentRequest.inProgress
    return (
      <Sticky positionRecheckInterval={0.1} mode='bottom'>
        <EuiPanel
          hasShadow={false}
          data-test-id='create-deployment-footer'
          css={css({
            padding: `${theme.euiTheme.size.l} ${theme.euiTheme.size.xxxxl}`,
            boxShadow: `0 -8px 8px -8px ${
              theme.colorMode === 'LIGHT'
                ? theme.euiTheme.colors.borderBasePlain
                : theme.euiTheme.colors.shadow
            }`,
          })}
        >
          {this.renderSummaryArchitecture()}
          <EuiFlexGroup justifyContent='spaceBetween'>
            {showSettings && region && deploymentTemplate && (
              <EuiFlexItem grow={false}>{this.renderArchitectureButton()}</EuiFlexItem>
            )}
            <EuiFlexItem grow={false}>
              <CreateDeploymentButton
                disabled={isFooterButtonDisabled}
                claimInstantStackDeployment={this.claimInstantStackDeployment}
                claimedInstantStackDeploymentId={claimedInstantStackDeploymentId}
                renderClaimStackDeploymentError={this.renderClaimStackDeploymentError}
                editorState={editorState}
                showApiRequest={true}
                onCreateDeployment={onCreateDeployment}
              />
            </EuiFlexItem>
          </EuiFlexGroup>
          {this.renderRequestErrors()}
        </EuiPanel>
      </Sticky>
    )
  }

  renderRequestErrors() {
    const { createStackDeploymentRequest, claimInstantStackDeploymentRequest } = this.props
    const createStackDeploymentRequestError = createStackDeploymentRequest.error
    const claimInstantStackDeploymentRequestError = claimInstantStackDeploymentRequest.error

    if (createStackDeploymentRequestError || claimInstantStackDeploymentRequestError) {
      return (
        <Fragment>
          <EuiSpacer />

          {createStackDeploymentRequestError && (
            <CuiAlert type='error'>{createStackDeploymentRequestError}</CuiAlert>
          )}
          {this.renderClaimStackDeploymentError(claimInstantStackDeploymentRequestError)}
        </Fragment>
      )
    }

    return null
  }

  renderClaimStackDeploymentError(error?: AsyncRequestError) {
    if (!error) {
      return null
    }

    if (!(error instanceof AjaxRequestError) || getResponseStatus(error) === 404) {
      return null
    }

    return <CuiAlert type='error'>{error}</CuiAlert>
  }

  renderArchitectureButton() {
    const { showPrice, region, editorState, profile } = this.props
    const { deploymentTemplate } = editorState
    const { showArchitectureSummary } = this.state

    if (showPrice && !inTrial({ profile })) {
      return (
        <PriceButton
          level={profile?.level}
          showArchitectureSummary={showArchitectureSummary}
          nodeConfigurations={this.getNodeConfigurations()}
          priceViewSelected={this.state.priceViewSelected}
          deploymentTemplate={deploymentTemplate!}
          regionId={region!.id}
          showPrice={showPrice}
          onChangePriceView={(id) => this.setState({ priceViewSelected: id })}
          onClickPriceButton={this.onClickPriceButton}
        />
      )
    }

    return (
      <div>
        <EuiButtonEmpty
          data-test-id='show-architecture-button'
          onClick={() =>
            this.setState({ showArchitectureSummary: !this.state.showArchitectureSummary })
          }
          iconSide='right'
          iconType={showArchitectureSummary ? 'arrowDown' : 'arrowUp'}
          size='s'
        >
          {showArchitectureSummary ? (
            <FormattedMessage
              id='architecture-button.hide-configuration'
              defaultMessage='Hide configuration'
            />
          ) : (
            <FormattedMessage
              id='architecture-button.show-configuration'
              defaultMessage='Show configuration'
            />
          )}
        </EuiButtonEmpty>
      </div>
    )
  }

  renderSummaryArchitecture() {
    const { editorState } = this.props
    const { showArchitectureSummary } = this.state
    const { regionId, deployment, deploymentTemplate } = editorState

    if (!deploymentTemplate || !regionId || !deployment) {
      return (
        <Fragment>
          <EuiSkeletonText />
          <EuiSpacer size='s' />
        </Fragment>
      )
    }

    const instanceConfigurations = deploymentTemplate!.instance_configurations
    const version = getUpsertVersion(editorState)

    return (
      <div
        css={css(
          { overflow: 'hidden' },
          showArchitectureSummary
            ? {
                maxHeight: '500px',
                transition: 'max-height .75s ease-in',
              }
            : {
                maxHeight: 0,
                visibility: 'hidden',
                transitionProperty: 'max-height, visibility',
                transitionDuration: '.55s',
                transitionTimingFunction: 'ease-out',
              },
        )}
      >
        <DeploymentArchitecture
          priceViewSelected={this.state.priceViewSelected}
          version={version!}
          regionId={regionId}
          autoscalingEnabled={getAutoscalingStatus({ deployment })}
          instanceConfigurations={instanceConfigurations}
          nodeConfigurations={this.getNodeConfigurations()}
        />

        <EuiHorizontalRule />
      </div>
    )
  }

  claimInstantStackDeployment = async (claimDeploymentRequestPayload) => {
    const { claimInstantStackDeployment, fetchDeployment } = this.props

    const claimedInstantStackDeploymentResponse = await claimInstantStackDeployment({
      payload: claimDeploymentRequestPayload,
    })
    const claimedDeploymentResponse: DeploymentClaimResponse =
      claimedInstantStackDeploymentResponse.payload
    const { deployment_id } = claimedDeploymentResponse

    this.setState({ claimedInstantStackDeploymentId: deployment_id })
    const fetchClaimedInstantStackDeploymentResponse = await fetchDeployment(deployment_id)
    const deployment: StackDeployment = fetchClaimedInstantStackDeploymentResponse.payload

    return deployment
  }

  onClickPriceButton = (): void => {
    this.setState({ showArchitectureSummary: !this.state.showArchitectureSummary })
  }

  onChangeSnapshotSource = (
    nextRestoreSnapshotSource?: DeploymentSearchResponse | null,
    regionId?: RegionId,
  ) => {
    const { onChange, editorState } = this.props

    if (nextRestoreSnapshotSource == null) {
      this.setState({ restoreSnapshotSource: null })
      changeRestoreFromSnapshot({ onChange, source: null })
      return
    }

    const disabledAutoscalingDeployment = setAutoscalingDisabled({
      deployment: editorState.deployment,
    })
    this.setState({ restoreSnapshotSource: nextRestoreSnapshotSource })

    // if we're restoring from a snapshot then we can't have autoscaling turned on, so we send the whole autoscaling turned off deployment
    changeRestoreFromSnapshot({
      onChange,
      regionId,
      source: nextRestoreSnapshotSource,
      baseDeployment: disabledAutoscalingDeployment,
    })
  }

  onChangeSnapshot = (nextSnapshot?: ClusterSnapshot | null) => {
    const { onChange } = this.props
    const { restoreSnapshotSource } = this.state
    changeRestoreFromSnapshot({
      onChange,
      source: restoreSnapshotSource,
      snapshotName: nextSnapshot ? nextSnapshot.snapshot : undefined,
    })
  }

  getNodeConfigurations(): AnyTopologyElement[] {
    const { editorState, stackVersions } = this.props

    const region = this.props.region!

    const payload = getCreatePayload({
      region,
      editorState,
      stackVersions,
    })

    return getDeploymentNodeConfigurations({ deployment: payload! })
  }

  updateEsSettings = (settings) => {
    const { onChange } = this.props
    setEsSettings({ onChange, settings })
  }

  shouldDisableBottomNav = (): boolean => {
    const { region, editorState, trialMaxedOut } = this.props
    const { deployment, regionId, _joltVersion } = editorState

    if (trialMaxedOut) {
      return true
    }

    if (
      deployment.settings?.observability &&
      !deployment.settings.observability.metrics &&
      !deployment.settings.observability.logging
    ) {
      return true
    }

    /*
     * A missing region — or a region mismatch — would indicate we haven't finished
     * fetching the currently selected region.
     */
    const actualRegionId = deployment && getRegionIdForCreate({ deployment })
    const missingRegion = !region || !regionId
    const regionMismatch = actualRegionId !== regionId

    if (missingRegion || regionMismatch) {
      return true
    }

    /*
     * `_joltVersion` is the currently selected version.
     * `actualVersion` comes from the deployment, based on the deployment template.
     * A mismatch means the template hasn't yet been fetched, and passed along
     * to the `editorState`.
     */
    const actualVersion = getUpsertVersion({ deployment, _fromJolt: false })
    const missingVersion = !_joltVersion || !actualVersion
    const versionMismatch = _joltVersion !== actualVersion

    if (missingVersion || versionMismatch) {
      return true
    }

    return false
  }

  getSelectTemplateStepCount() {
    const { showRegion } = this.props

    let count = 3 // name step, setup step, optimize step

    if (showRegion) {
      count += 2 // platform step, region step
    }

    return count // NOTE: pricing step doesn't count!
  }

  getSnapshotDetails() {
    const { restoreSnapshotSource } = this.state
    const { showRegion } = this.props
    const firstEs = restoreSnapshotSource
      ? getFirstEsClusterFromGet({ deployment: restoreSnapshotSource })
      : null
    const snapshotSourceRegionId = restoreSnapshotSource
      ? getRegionId({ deployment: restoreSnapshotSource })
      : null
    const latestSnapshotDate = firstEs ? getLatestSnapshotSuccess({ resource: firstEs }) : null
    return restoreSnapshotSource === null
      ? {}
      : {
          deploymentId: firstEs!.id,
          deploymentName: restoreSnapshotSource.name,
          regionId: showRegion ? snapshotSourceRegionId : null,
          snapshotDate: latestSnapshotDate || ``,
        }
  }
}

export default withEuiTheme(injectIntl(withLDConsumer()(CreateStackDeploymentEditor)))
